import React, { useState, useEffect } from 'react';
import { Calendar, Award, TrendingUp, Globe, Briefcase } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import axios from 'axios';
import PageTitle from '../hooks/PageTitle';
import CaseStudiesSkeletonLoader from '../components/skeletons/CaseStudiesSkeletonLoader';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CaseStudies = () => {
  const [caseStudies, setCaseStudies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchCaseStudies();
  }, []);

  const fetchCaseStudies = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/api/case-studies/`);
      setCaseStudies(response.data);
      setError(null);
    } catch (err) {
      console.error('Error fetching case studies:', err);
      setError('Unable to load case studies. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <CaseStudiesSkeletonLoader />;
  }

  if (error) {
    return (
      <div className="min-h-screen pt-20 flex items-center justify-center">
      <PageTitle title="Case Studies" />
        <div className="text-center max-w-md">
          <div className="bg-red-50 border border-red-200 rounded-lg p-6">
            <p className="text-red-800">{error}</p>
            <button 
              onClick={fetchCaseStudies}
              className="mt-4 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
            >
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-20">
      <PageTitle title="Case Studies" />
      {/* Hero Section */}
      <section className="relative py-24 bg-gray-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <h1 className="text-5xl md:text-6xl font-serif mb-6">
              Case Studies
            </h1>
            <p className="text-xl text-gray-300 leading-relaxed">
              A selection of our recent significant matters demonstrating our expertise and results
            </p>
          </div>
        </div>
      </section>

      {/* Disclaimer */}
      <section className="py-8 bg-gray-50 border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-sm text-gray-600 italic text-center">
            Client confidentiality is paramount. These case studies represent illustrative examples with identifying details modified to protect client privacy.
          </p>
        </div>
      </section>

      {/* Case Studies */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {caseStudies.length === 0 ? (
            <div className="text-center py-12">
              <Briefcase className="h-16 w-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No case studies available</h3>
              <p className="text-gray-600">I case studies saranno pubblicati a breve</p>
            </div>
          ) : (
            <div className="space-y-12">
              {caseStudies.map((study) => (
                <Card key={study.id || study._id} className="border-0 shadow-xl hover:shadow-2xl transition-all duration-300">
                  <CardContent className="p-10">
                    <div className="flex flex-col lg:flex-row lg:items-start lg:space-x-8">
                  <div className="flex-1">
                        <div className="flex flex-wrap items-center gap-3 mb-4">
                          {study.sector && <Badge className="bg-gray-900">{study.sector}</Badge>}
                          {study.practice_area && <Badge variant="outline">{study.practice_area}</Badge>}
                        </div>
                        
                        <h3 className="text-3xl font-serif text-gray-900 mb-4">
                          {study.title}
                        </h3>
                        
                        {study.challenge && (
                          <div className="mb-6">
                            <h4 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-2">The Challenge</h4>
                            <p className="text-gray-600 leading-relaxed">
                              {study.challenge}
                            </p>
                          </div>
                        )}

                        {study.solution && (
                          <div className="mb-6">
                            <h4 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-2">The Solution</h4>
                            <p className="text-gray-600 leading-relaxed">
                              {study.solution}
                            </p>
                          </div>
                        )}
                        
                        {study.outcome && (
                          <div className="bg-green-50 border-l-4 border-green-500 p-4 rounded">
                            <div className="flex items-start">
                              <Award className="h-5 w-5 text-green-600 mt-0.5 mr-3 flex-shrink-0" />
                              <div>
                                <h4 className="text-sm font-semibold text-green-900 mb-1">Outcome</h4>
                                <p className="text-sm text-green-800">
                                  {study.outcome}
                                </p>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default CaseStudies;